/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PÁGINA: Firmantes Externos — BIOFIRMAS CTX
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Consulta y gestión de firmas capturadas desde sistemas externos (HC, etc.)
 * Solo visible para superadmin.
 * 
 * Ruta en App.js:
 *   <Route path="/firmantes-externos" element={<ExternalFirmantesPage />} />
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import apiClient from '../utils/apiConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

function ExternalFirmantesPage() {
  const [sidebarAbierto, setSidebarAbierto]   = useState(true);
  const [firmantes, setFirmantes]             = useState([]);
  const [cargando, setCargando]               = useState(true);
  const [buscar, setBuscar]                   = useState('');
  const [filtroSistema, setFiltroSistema]     = useState('');
  const [sistemas, setSistemas]               = useState([]);
  const [toast, setToast]                     = useState({ msg: '', tipo: '' });

  // Modal ver firma
  const [modalFirma, setModalFirma]           = useState(null);
  const [cargandoFirma, setCargandoFirma]     = useState(false);

  const correoUsuario = localStorage.getItem('correo') || '';
  const rol           = localStorage.getItem('rol')    || '';

  // ── Toast ──
  const mostrarToast = (msg, tipo = 'success') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast({ msg: '', tipo: '' }), 4000);
  };

  // ── Cargar firmantes ──
  const cargarFirmantes = useCallback(async () => {
    setCargando(true);
    try {
      const params = {};
      if (buscar)       params.buscar  = buscar;
      if (filtroSistema) params.sistema = filtroSistema;

      const res = await apiClient.get('/api/external/firma/firmantes', {
        params,
        headers: {
          'usuario-correo': correoUsuario,
          'usuario-rol':    rol,
          'rol':            rol
        }
      });

      const data = res.data.firmantes || [];
      setFirmantes(data);

      // Extraer sistemas únicos para el filtro
      const s = [...new Set(data.map(f => f.sistemaOrigen))].filter(Boolean);
      setSistemas(s);

    } catch (err) {
      mostrarToast('Error al cargar firmantes externos', 'error');
    } finally {
      setCargando(false);
    }
  }, [buscar, filtroSistema, correoUsuario, rol]);

  useEffect(() => {
    cargarFirmantes();
  }, [cargarFirmantes]);

  // ── Ver firma ──
  const handleVerFirma = async (cedula, nombres) => {
    setCargandoFirma(true);
    setModalFirma({ cedula, nombres, firma: null });
    try {
      const res = await apiClient.get(`/api/external/firma/firmantes/${cedula}`, {
        headers: { 'usuario-correo': correoUsuario, 'usuario-rol': rol, 'rol': rol }
      });
      setModalFirma({ cedula, nombres, firma: res.data.firmante?.firma });
    } catch {
      mostrarToast('Error al cargar la firma', 'error');
      setModalFirma(null);
    } finally {
      setCargandoFirma(false);
    }
  };

  // ── Eliminar firma ──
  const handleEliminar = async (cedula, nombres) => {
    if (!window.confirm(`¿Eliminar la firma de ${nombres}?\n\nEl funcionario deberá registrarla nuevamente.`)) return;
    try {
      await apiClient.delete(`/api/external/firma/firmantes/${cedula}`, {
        headers: { 'usuario-correo': correoUsuario, 'usuario-rol': rol, 'rol': rol }
      });
      mostrarToast(`Firma de ${nombres} eliminada`, 'success');
      cargarFirmantes();
    } catch {
      mostrarToast('Error al eliminar la firma', 'error');
    }
  };

  // ── Formato fecha ──
  const fmtFecha = (f) => {
    if (!f) return '—';
    return new Date(f).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // ── Badge sistema ──
  const badgeSistema = (s) => {
    const colores = {
      'historia-clinica': { bg: '#e0f2fe', color: '#0369a1' },
      'sistema-rrhh':     { bg: '#f3e8ff', color: '#7e22ce' },
    };
    const c = colores[s] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{ background: c.bg, color: c.color, padding: '3px 12px',
        borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
        {s || '—'}
      </span>
    );
  };

  return (
    <>
      <style>{`
        .tabla-row:hover { background: #f8faff !important; }
        .firma-thumb { width: 80px; height: 40px; object-fit: contain; border: 1px solid #e5e7eb; border-radius: 6px; background: white; }
      `}</style>

      {/* Toast */}
      {toast.msg && (
        <div style={{
          position: 'fixed', top: 20, right: 24, zIndex: 9999,
          background: toast.tipo === 'error' ? '#dc3545' : '#198754',
          color: '#fff', padding: '12px 20px', borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 10, minWidth: 280
        }}>
          <i className={`bi ${toast.tipo === 'error' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'}`}></i>
          {toast.msg}
        </div>
      )}

      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />

        <div className="flex-grow-1" style={{
          marginLeft: sidebarAbierto ? '250px' : '70px',
          transition: 'margin-left 0.3s',
          padding: '28px 36px',
          minHeight: '100vh',
          background: '#f0f4f8'
        }}>

          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1a1e4b' }}>
                🔏 Firmantes Externos
              </h2>
              <p className="text-muted mb-0 small">
                Firmas capturadas desde sistemas externos vía API
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <span className="badge" style={{ background: '#1a1e4b', fontSize: '0.85rem', padding: '7px 14px' }}>
                {firmantes.length} registros
              </span>
              <button className="btn btn-sm btn-outline-secondary" onClick={cargarFirmantes}>
                <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body py-3 px-4">
              <div className="row g-3 align-items-end">
                <div className="col-md-5">
                  <label className="form-label fw-semibold small mb-1">🔍 Buscar</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nombre, cédula o correo..."
                      value={buscar}
                      onChange={e => setBuscar(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && cargarFirmantes()}
                    />
                    {buscar && (
                      <button className="btn btn-outline-secondary" onClick={() => setBuscar('')}>✕</button>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold small mb-1">🔗 Sistema origen</label>
                  <select className="form-select" value={filtroSistema} onChange={e => setFiltroSistema(e.target.value)}>
                    <option value="">Todos los sistemas</option>
                    {sistemas.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <button className="btn btn-outline-secondary w-100" onClick={() => { setBuscar(''); setFiltroSistema(''); }}>
                    🗑️ Limpiar filtros
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
            <div className="card-body p-0">
              {cargando ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                  <p className="text-muted">Cargando firmantes...</p>
                </div>
              ) : firmantes.length === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: '3rem' }}>🔏</div>
                  <p className="text-muted mt-2 mb-0">No se encontraron firmantes externos</p>
                  <small className="text-muted">
                    {buscar || filtroSistema ? 'Intenta con otros filtros' : 'Aún no hay firmas registradas desde sistemas externos'}
                  </small>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fw-semibold">Funcionario</th>
                        <th className="fw-semibold">Cédula</th>
                        <th className="fw-semibold text-center">Sistema</th>
                        <th className="fw-semibold text-center">Firma</th>
                        <th className="fw-semibold text-center">Consultas</th>
                        <th className="fw-semibold">Registrado</th>
                        <th className="fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {firmantes.map((f) => (
                        <tr key={f.cedula} className="tabla-row" style={{ verticalAlign: 'middle' }}>
                          <td>
                            <div className="fw-semibold">{f.nombres}</div>
                            <small className="text-muted">{f.correo}</small>
                          </td>
                          <td>
                            <code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4, fontSize: '0.82rem' }}>
                              {f.cedula}
                            </code>
                          </td>
                          <td className="text-center">
                            {badgeSistema(f.sistemaOrigen)}
                          </td>
                          <td className="text-center">
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle-fill me-1"></i>Registrada
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-secondary" style={{ fontSize: '0.8rem' }}>
                              {f.vecesConsultada || 0}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">{fmtFecha(f.createdAt)}</small>
                            {f.ultimaConsulta && (
                              <div>
                                <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                                  Última consulta: {fmtFecha(f.ultimaConsulta)}
                                </small>
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Ver firma"
                                onClick={() => handleVerFirma(f.cedula, f.nombres)}
                              >
                                👁️
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Eliminar firma (el funcionario deberá volver a firmar)"
                                onClick={() => handleEliminar(f.cedula, f.nombres)}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── MODAL VER FIRMA ── */}
      {modalFirma && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 16 }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold" style={{ color: '#1a1e4b' }}>
                    🔏 Firma de {modalFirma.nombres}
                  </h5>
                  <small className="text-muted">CC {modalFirma.cedula}</small>
                </div>
                <button className="btn-close" onClick={() => setModalFirma(null)}></button>
              </div>
              <div className="modal-body text-center py-4">
                {cargandoFirma ? (
                  <div className="py-4">
                    <div className="spinner-border text-primary"></div>
                    <p className="text-muted mt-2">Cargando firma...</p>
                  </div>
                ) : modalFirma.firma ? (
                  <>
                    <div style={{
                      background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: 12, padding: 20, display: 'inline-block'
                    }}>
                      <img
                        src={modalFirma.firma}
                        alt="Firma"
                        style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
                      />
                    </div>
                    <p className="text-muted small mt-3 mb-0">
                      Firma combinada con sello corporativo de Colombiana de Trasplantes S.A.S.
                    </p>
                  </>
                ) : (
                  <p className="text-muted">No se pudo cargar la firma.</p>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary" onClick={() => setModalFirma(null)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ExternalFirmantesPage;